import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTasks, changeTaskStatus, getUsers } from '../utils/api';
import TaskDrawer from '../components/TaskDrawer';
import { motion } from 'framer-motion';

const COLUMNS = [
  { key: 'pendiente',  label: 'Pendiente',  icon: '○', color: '#64748B', bg: 'rgba(100,116,139,0.08)', statuses: ['Pendiente'] },
  { key: 'en_curso',   label: 'En Curso',   icon: '▶', color: '#3B82F6', bg: 'rgba(59,130,246,0.08)',  statuses: ['En progreso', 'Recurrente'] },
  { key: 'bloqueada',  label: 'Bloqueada',  icon: '🚫', color: '#EF4444', bg: 'rgba(239,68,68,0.08)',  statuses: ['Bloqueada'] },
  { key: 'en_espera',  label: 'En Espera',  icon: '⏳', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', statuses: ['En espera de cliente', 'En espera de usuario/Presidencia'] },
  { key: 'revision',   label: 'Revisión',   icon: '👁', color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)', statuses: ['Lista para revisión', 'Devuelta / Requiere ajustes'] },
  { key: 'cerradas',   label: 'Cerradas',   icon: '✓',  color: '#10B981', bg: 'rgba(16,185,129,0.08)', statuses: ['Aprobada / Cerrada', 'No realizada'] },
];

const PRIORITY_DOT = {
  'Crítica': '#EF4444',
  'Alta':    '#F97316',
  'Media':   '#EAB308',
  'Baja':    '#6366F1',
};

const KanbanCard = ({ task, users, onClick }) => {
  const responsible = users.find(u => u.id === task.responsible_user_id);
  return (
    <motion.div
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(task)}
      className="rounded-xl p-3 cursor-pointer"
      style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
    >
      {/* Responsabilidad */}
      {task.responsabilidad && (
        <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: '#CBD5E1' }}>
          {task.responsabilidad}
        </p>
      )}
      {/* Título */}
      <p className="text-sm font-semibold leading-snug mb-2.5" style={{ color: '#0F172A' }}>{task.title}</p>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: PRIORITY_DOT[task.priority] || '#94A3B8' }} />
          <span className="text-xs font-semibold" style={{ color: '#94A3B8' }}>{task.priority}</span>
        </div>
        {responsible && (
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#6366F1,#818CF8)' }}>
              {responsible.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs font-medium" style={{ color: '#64748B' }}>{responsible.name.split(' ')[0]}</span>
          </div>
        )}
      </div>

      {task.status === 'Recurrente' && (
        <div className="mt-2 text-xs font-bold px-2 py-0.5 rounded-full inline-block"
          style={{ background: 'rgba(6,182,212,0.1)', color: '#06B6D4' }}>
          🔁 Recurrente
        </div>
      )}
      {task.status === 'No realizada' && (
        <div className="mt-2 text-xs font-bold px-2 py-0.5 rounded-full inline-block"
          style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
          ✕ No realizada
        </div>
      )}
    </motion.div>
  );
};

const KanbanColumn = ({ col, tasks, users, onTaskClick }) => {
  // Agrupar tarjetas por responsabilidad dentro de la columna
  const groups = {};
  tasks.forEach(t => {
    const key = t.responsabilidad || 'Sin responsabilidad';
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });
  const groupKeys = Object.keys(groups).sort();
  const multiGroup = groupKeys.length > 1;

  return (
    <div className="flex flex-col min-w-[240px] w-60 rounded-xl overflow-hidden"
      style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.07)' }}>
      {/* Header columna */}
      <div className="px-3 py-2.5 flex items-center justify-between" style={{ background: col.bg, borderBottom: `2px solid ${col.color}22` }}>
        <div className="flex items-center gap-2">
          <span className="text-sm">{col.icon}</span>
          <h3 className="text-sm font-bold" style={{ color: col.color }}>{col.label}</h3>
        </div>
        <span className="text-xs font-black tabular-nums px-2 py-0.5 rounded-full"
          style={{ background: tasks.length > 0 ? col.color + '20' : 'rgba(0,0,0,0.05)', color: tasks.length > 0 ? col.color : '#94A3B8' }}>
          {tasks.length}
        </span>
      </div>
      {/* Cards agrupadas por responsabilidad */}
      <div className="p-2 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 260px)' }}>
        {tasks.length === 0 ? (
          <div className="text-center py-8 text-xs" style={{ color: '#CBD5E1' }}>Sin tareas</div>
        ) : (
          <div className="space-y-3">
            {groupKeys.map(key => (
              <div key={key}>
                {multiGroup && (
                  <p className="text-[9px] font-bold uppercase tracking-widest px-1 pb-1.5 truncate"
                    style={{ color: col.color, opacity: 0.7 }}>
                    {key}
                  </p>
                )}
                <div className="space-y-2">
                  {groups[key].map(task => (
                    <KanbanCard key={task.id} task={task} users={users} onClick={onTaskClick} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Kanban = () => {
  const { user } = useAuth();
  const [tasks, setTasks]         = useState([]);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.allSettled([getTasks(), getUsers()]);
      if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value.data || []);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data || []);
    } finally { setLoading(false); }
  };

  const getColumnTasks = (col) => tasks.filter(t => col.statuses.includes(t.status));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const active = tasks.filter(t => !['Aprobada / Cerrada', 'No realizada'].includes(t.status)).length;
  const closed = tasks.filter(t => t.status === 'Aprobada / Cerrada').length;

  return (
    <div className="min-h-screen" style={{ background: '#F1F5F9' }}>
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-border bg-background sticky top-14 md:top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-foreground">Kanban</h1>
            <p className="text-xs text-muted-foreground">
              {active} activas · {closed} cerradas
            </p>
          </div>
          <button onClick={loadData} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* Board */}
      <div className="p-3 md:p-5 overflow-x-auto pb-20 md:pb-5">
        <div className="flex gap-3 min-w-max">
          {COLUMNS.map(col => (
            <KanbanColumn
              key={col.key}
              col={col}
              tasks={getColumnTasks(col)}
              users={users}
              onTaskClick={setSelectedTask}
            />
          ))}
        </div>
      </div>

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default Kanban;
